import { useLoaderData, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { object, string } from 'yup';

import { Json, Owner } from '../models/json';
import { Status, Vehicle } from '../models/vehicle';
import { DocumentData } from '../../../iveco/recall-campaign/vehicle/models/document-data';
import agent from '../../../../api/agent';
import { Static } from '../../../../static';
import { useStore } from '../../../../context/store';
import MainForm from './MainForm';

const validationSchema = object().shape({
	surname: string().required('Il cognome è obbligatorio'),
	name: string().required('Il nome è obbligatorio'),
	address: string().required("L'indirizzo è obbligatorio"),
	cap: string().required('Il CAP è obbligatorio'),
	city: string().required('La città è obbligatoria'),
	streetNumber: string().required('Il numero civico è obbligatorio'),
	province: string().required('La provincia è obbligatoria'),
});

const StellantisMain = () => {
	const loaderData = useLoaderData() as Json | null;
	const [baseJson, setBaseJson] = useState<Json | null>(loaderData);
	const [json, setJson] = useState<Json | null>(
		loaderData ? JSON.parse(JSON.stringify(loaderData)) : null,
	);
	const [selectedGuuid, setSelectedGuuid] = useState<string>(
		loaderData?.Vehicles?.[0]?.Guuid ?? '',
	);
	const [savedGuids, setSavedGuids] = useState<string[]>([]);
	const vehicles = json?.Vehicles ?? [];
	const baseVehicles = baseJson?.Vehicles ?? [];
	const vehicle = selectedGuuid
		? (vehicles.find((x) => x.Guuid === selectedGuuid) ?? null)
		: null;
	const baseVehicle = baseVehicles.find((x) => x.Guuid === selectedGuuid);
	const modifiedVehicle = !!baseVehicle?.Status?.Key;

	const { loaderStore, modalStore } = useStore();
	const { id } = useParams();
	const vehicleOptions = vehicles.map((x) => {
		return {
			guuid: x.Guuid,
			label: `${x.Chassis || '-'} - ${x.LicensePlate || '-'}`,
		};
	});

	const initialValues = {
		surname: vehicle?.NewOwner?.Surname || '',
		name: vehicle?.NewOwner?.Name || '',
		address: vehicle?.NewOwner?.Address || '',
		cap: vehicle?.NewOwner?.Cap || '',
		city: vehicle?.NewOwner?.City || '',
		streetNumber: vehicle?.NewOwner?.StreetNumber || '',
		province: vehicle?.NewOwner?.Province || '',
	};

	const resetUnsavedVehicle = () => {
		if (
			json &&
			baseJson &&
			selectedGuuid &&
			!savedGuids.includes(selectedGuuid)
		) {
			const currentIndex = json.Vehicles.findIndex(
				(x) => x.Guuid === selectedGuuid,
			);
			const originalVehicle = baseJson.Vehicles.find(
				(x) => x.Guuid === selectedGuuid,
			);
			if (currentIndex === -1 || !originalVehicle) return;

			const veh = JSON.parse(JSON.stringify([...json.Vehicles]));
			veh[currentIndex] = JSON.parse(JSON.stringify(originalVehicle));
			setJson((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					Vehicles: veh,
				};
			});
		}
	};

	const vehicleSelectOnChange = (
		event: React.ChangeEvent<HTMLSelectElement>,
	) => {
		resetUnsavedVehicle();
		setSelectedGuuid(event.target.value);
	};

	const submit = async (values: any, actions: FormikHelpers<any> | null) => {
		const vehicleIndex = vehicles.findIndex((v) => v.Guuid === vehicle?.Guuid);
		if (!json || !vehicle || vehicleIndex === -1) {
			actions?.setSubmitting(false);
			toast.error(Static.saveFailed);
			return;
		}

		const updatedVehicles = [...(json?.Vehicles ?? [])];
		const updatedVehicle: Vehicle = { ...updatedVehicles[vehicleIndex] };
		updatedVehicle.Status = new Status();
		updatedVehicle.Status.Key = vehicle.Status?.Key;
		updatedVehicle.Status.Value =
			vehicle.Status?.Value ??
			json.StatusOptions.find((x) => x.Key === vehicle.Status?.Key)?.Value;

		if (isFormVisible(updatedVehicle.Status.Key)) {
			const newOwner = new Owner();
			newOwner.Surname = values.surname;
			newOwner.Name = values.name;
			newOwner.Address = values.address;
			newOwner.Cap = values.cap;
			newOwner.City = values.city;
			newOwner.StreetNumber = values.streetNumber;
			newOwner.Province = values.province;

			updatedVehicle.NewOwner = newOwner;
		} else {
			updatedVehicle.NewOwner = null;
		}

		updatedVehicles[vehicleIndex] = updatedVehicle;

		const updatedJson: Json = { ...json, Vehicles: updatedVehicles };
		setJson(updatedJson);

		let documentData = new DocumentData();
		documentData.json = JSON.stringify(updatedJson);
		documentData.documentId = Number(id);
		loaderStore.setAppLoadingTrue();
		agent.Document.saveDocumentData(documentData)
			.then((res) => {
				if (res === true) {
					toast.success(Static.thanks);
					setSavedGuids((prev) => {
						return prev.includes(vehicle.Guuid)
							? prev
							: [...prev, vehicle.Guuid];
					});
					setBaseJson(updatedJson);
				} else toast.error(Static.saveFailed);

				actions?.setSubmitting(false);
				loaderStore.setAppLoadingFalse();
			})
			.catch((ex) => {
				actions?.setSubmitting(false);
				loaderStore.setAppLoadingFalse();
				toast.error(Static.anUnexpectedErrorHasOccurred);
			});
	};

	const statusChange = (key: string) => {
		if (!json || !vehicle || !selectedGuuid) return;

		const v = { ...vehicle } as Vehicle;
		const intKey = Number(key);

		if (intKey) {
			v.Status = new Status();
			v.Status.Key = intKey;
			v.Status.Value = json.StatusOptions.find((x) => x.Key === intKey)?.Value!;
		} else {
			v.Status = new Status();
			v.Status.Key = undefined;
			v.Status.Value = undefined;
		}
		const vehicles = [...(json?.Vehicles ?? [])];
		const vehicleIndex = vehicles.findIndex((x) => x.Guuid === selectedGuuid);
		if (vehicleIndex === -1) return;

		vehicles[vehicleIndex] = v;
		setJson((old) => {
			if (!old) return old;
			return { ...old, Vehicles: vehicles };
		});
	};

	const privacyClick = () => {
		modalStore.openModal(
			<div className='p-3'>
				<div className='text-end'>
					<button
						type='button'
						className='icon-button'
						onClick={modalStore.closeModal}
						aria-label='Chiudi finestra'
					>
						<i className='fa-solid fa-x' aria-hidden='true'></i>
					</button>
				</div>
				<br />
				<div
					className='stellantis-privacy-policy'
					dangerouslySetInnerHTML={{ __html: Static.stellantisAndPsaPrivacy }}
				></div>
			</div>,
			'dark-bg-color rounded text-white',
		);
	};

	const isFormVisible = (key: number | undefined) => {
		if (!key) return false;
		return (
			json?.StatusOptions.find((x) => x.Key === key)?.IsFormVisible || false
		);
	};

	const brandLogoPath = json?.Brand
		? `${json.Brand.toLowerCase().trim().replace(/\s+/g, '')}.svg`
		: undefined;
	const hasDocumentData = !!json;
	const hasVehicles = vehicles.length > 0;

	return (
		<div className='d-flex flex-column' style={{ minHeight: 'inherit' }}>
			<div className='stellantis-hero bg-black'>
				<div className='stellantis-hero__content'>
					{brandLogoPath && (
						<img
							src={`/images/stellantis/${brandLogoPath}`}
							className='brand-logo stellantis-hero__brand'
							alt=''
						/>
					)}
					<div>
						<h1 className='stellantis-hero__greeting text-white'>Buongiorno</h1>
						{json?.Dealership && (
							<h1 className='stellantis-hero__dealership text-white fw-bold'>
								{json.Dealership}
							</h1>
						)}
					</div>
				</div>
			</div>
			<div id='main-document' className='main-gradient flex-grow-1'>
				<div className='stellantis-summary'>
					<div>
						<div className='text-white h3 fw-bold py-md-4 py-3'>
							VARIAZIONI DI POSSESSO
						</div>
						{json?.CampaignNumber && (
							<div className='h4 text-white'>
								CAMPAGNA N°: {json.CampaignNumber}
							</div>
						)}
					</div>
				</div>

				{!hasDocumentData && (
					<div className='stellantis-empty-state'>
						Dati documento non presenti.
					</div>
				)}
				{hasDocumentData && !hasVehicles && (
					<div className='stellantis-empty-state'>Veicolo non presente.</div>
				)}
				{hasVehicles && (
					<>
						<div className='row mt-md-4 mt-3'>
							<div className='col-12'>
								<select
									className='form-select p-3 fw-bold mt-3'
									onChange={(e) => vehicleSelectOnChange(e)}
									value={selectedGuuid}
								>
									<option value=''>Seleziona il veicolo</option>
									{vehicleOptions.map((x) => {
										return (
											<option key={x.guuid} value={x.guuid}>
												{x.label}
											</option>
										);
									})}
								</select>
							</div>
						</div>
						{vehicle && modifiedVehicle && (
							<div className='stellantis-status-alert' role='status'>
								Questo veicolo è stato dichiarato
								{baseVehicle?.Status?.Value
									? ` "${baseVehicle.Status.Value.toLowerCase()}"`
									: ''}
								, quindi non è più modificabile.
							</div>
						)}
						{vehicle && (
							<div className='row my-4'>
								<div className='col-md-3 text-white text-lg mt-3'>
									Il Veicolo è stato
								</div>
								<div className='col-md-9 mt-3'>
									<div className='app-radio-group'>
										{(json?.StatusOptions ?? []).map((op) => (
											<label
												key={op.Key}
												className={`app-radio ${
													modifiedVehicle ? 'is-disabled' : ''
												}`}
											>
												<input
													name='stellantis-status'
													type='radio'
													value={String(op.Key)}
													checked={vehicle?.Status?.Key === op.Key}
													disabled={modifiedVehicle}
													onChange={(e) => statusChange(e.target.value)}
												/>
												<span className='app-radio__dot' aria-hidden='true' />
												<span className='app-radio__text'>{op.Value}</span>
											</label>
										))}
									</div>
								</div>
							</div>
						)}
					</>
				)}
				{vehicle && isFormVisible(vehicle?.Status?.Key) && (
					<Formik
						validateOnChange
						enableReinitialize
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={submit}
					>
						<MainForm privacyClick={privacyClick} disabled={modifiedVehicle} />
					</Formik>
				)}
				{vehicle && !isFormVisible(vehicle?.Status?.Key) && (
					<>
						<p className='text-white fst-italic' style={{ fontSize: '0.8rem' }}>
							Cliccando sul pulsante "Conferma" dichiaro di aver letto e
							accettato le condizioni sul trattamento dei dati. Per maggiori
							informazioni clicca su{' '}
							<button
								type='button'
								className='privacy-link text-decoration-underline'
								onClick={privacyClick}
							>
								INFORMATIVA PRIVACY
							</button>
						</p>
						<div className='col-md-2 d-flex mt-4'>
							<button
								className='btn flex-grow-1'
								type='button'
								disabled={
									(!vehicle?.Status?.Key && !vehicle?.Status?.Value) ||
									modifiedVehicle
								}
								onClick={() => submit(null, null)}
							>
								Conferma
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default StellantisMain;
